import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import {
  MediaFile,
  MediaUploadOptions,
  MediaUploadProgress,
} from '../types/media.types';

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/*', 'video/*', 'application/pdf'];

export class MediaService {
  private static storage = getStorage();

  /**
   * Upload a media file to Firebase Storage with progress tracking
   * @param userId User ID
   * @param file Media file to upload
   * @param options Upload options
   * @param onProgress Progress callback
   * @param testId Optional test ID to store in test-specific folder
   * @returns Promise with download URL
   */
  static async uploadMedia(
    userId: string,
    file: MediaFile,
    options: MediaUploadOptions = {},
    onProgress?: (progress: MediaUploadProgress) => void,
    testId?: string
  ): Promise<string> {
    const { maxSize = MAX_FILE_SIZE, acceptedTypes = ACCEPTED_TYPES } = options;

    // Validate file
    if (file.file.size > maxSize) {
      throw new Error(`File size exceeds ${maxSize / (1024 * 1024)}MB limit`);
    }

    if (!acceptedTypes.some((type) => file.file.type.match(type))) {
      throw new Error('File type not supported');
    }

    // Generate unique path
    const fileId = uuidv4();
    const extension = file.file.name.split('.').pop();

    // Determine the storage path based on whether a testId is provided
    let path: string;
    if (testId) {
      // Store in test-specific folder
      path = `tests/${testId}/images/${fileId}-original.${extension}`;
      console.log('Storing image in test folder:', path);
    } else if (options.folder) {
      // Store in specified folder
      path = `user-media/${userId}/${options.folder}/${fileId}-original.${extension}`;
    } else {
      // Default user media path
      path = `user-media/${userId}/${fileId}/original.${extension}`;
    }

    const storageRef = ref(this.storage, path);

    // Upload with progress tracking
    const uploadTask = uploadBytesResumable(storageRef, file.file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress?.({
            id: fileId,
            progress,
            status: 'uploading',
          });
        },
        (error) => {
          onProgress?.({
            id: fileId,
            progress: 0,
            status: 'error',
            error: error.message,
          });
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            onProgress?.({
              id: fileId,
              progress: 100,
              status: 'completed',
            });
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  /**
   * Delete a media file from storage
   */
  static async deleteMedia(path: string): Promise<void> {
    const fileRef = ref(this.storage, path);
    await deleteObject(fileRef);
  }

  /**
   * Get the download URL for a media file
   */
  static async getMediaUrl(path: string): Promise<string> {
    const fileRef = ref(this.storage, path);
    return getDownloadURL(fileRef);
  }
}
